/**
 * Frontend API client for the AI tab.
 * Maps 1:1 to /v1/observe/ai/* in backend/product/src/api/server.ts.
 */

import { request } from './client';

// ── Types ───────────────────────────────────────────────────────────────────

export interface ModelMetrics {
  calls: number;
  ok: number;
  failures: number;
  costGbp: number;
  avgLatencyMs: number;
  outcomes: Record<string, number>;
}

export interface AiDailyRollup {
  day: string;
  calls: number;
  ok: number;
  failures: number;
  costGbp: number;
  tokensIn: number;
  tokensOut: number;
  cachedIn: number;
  cacheHitRate: number;
  escalations: number;
  p50LatencyMs: number;
  p95LatencyMs: number;
  outcomes: Record<string, number>;
  models?: Record<string, ModelMetrics>;
}

export interface AiBurn {
  gbpPerDay: number;
  gbpLast24h: number;
  gbpWindow: number;
  daysObserved: number;
}

export interface AiRunway {
  creditGbp: number | null;
  days: number | null;
  reason: 'ok' | 'no_credit_set' | 'no_burn' | 'no_data';
  /** Always false today — DashScope does not expose a balance on our key. */
  balanceFromProvider: boolean;
}

export interface AiUsage {
  windowDays: number;
  totals: {
    calls: number; ok: number; failures: number;
    costGbp: number; tokensIn: number; tokensOut: number; cachedIn: number;
  };
  cacheHitRate: number;
  imageCacheHitRate: number;
  escalationRate: number;
  failureRate: number;
  p50LatencyMs: number;
  p95LatencyMs: number;
  outcomes: Record<string, number>;
  gbpPer1kClassified: number;
  gbpPerEmit: number | null;
  emitsInWindow: number;
  burn: AiBurn;
  runway: AiRunway;
  daily: AiDailyRollup[];
  models?: Record<string, ModelMetrics>;
}

export interface AiStatus {
  ok: true;
  enabled: boolean;
  shadowMode: boolean;
  /** EFFECTIVE switch state, from the running process — not the host .env.
   *  A switch changed after `startedAt` is not in effect yet. */
  gates?: { image: boolean; device: boolean; category: boolean; modelKey: boolean; specFill: boolean };
  canDecide?: boolean;
  startedAt?: string;
  model: string | null;
  escalationModel: string | null;
  sampleRate: number | null;
  dailyCeilingGbp: number | null;
  verdictVersion: string;
  runtime: {
    breaker: { failures: number; open: boolean };
    spend: { day: string; spentGbp: number; ceilingGbp: number };
    imageCacheSize: number;
  } | null;
}

export interface AiPrompt {
  ok: true;
  basePromptVersion: string;
  effectivePromptVersion: string;
  revision: number;
  updatedAt: string | null;
  updatedBy: string | null;
  note: string | null;
  isOverridden: boolean;
  categories: string[];
  defaultSystemPrompt: string;
  effectiveSystemPrompt: string;
  userPromptExample: string;
}

// ── Calls ───────────────────────────────────────────────────────────────────

export async function fetchAiUsage(days = 30): Promise<AiUsage> {
  const r = await request<{ ok: true; usage: AiUsage }>(`/v1/observe/ai/usage?days=${days}`);
  return r.usage;
}

export function fetchAiStatus(): Promise<AiStatus> {
  return request<AiStatus>('/v1/observe/ai/status');
}

export function fetchAiPrompt(): Promise<AiPrompt> {
  return request<AiPrompt>('/v1/observe/ai/prompt');
}

export interface SavePromptResult {
  ok: true;
  revision: number;
  effectivePromptVersion: string;
  isOverridden: boolean;
}

/** `systemPrompt: null` reverts to the prompt that ships in code. */
export function saveAiPrompt(input: {
  systemPrompt: string | null;
  note?: string;
  updatedBy?: string;
}): Promise<SavePromptResult> {
  return request<SavePromptResult>('/v1/observe/ai/prompt', { method: 'POST', body: input });
}

export interface ComparisonCounts {
  agree: number; disagree: number; ai_only: number;
  regex_only: number; neither: number; total: number;
}
export interface ComparisonBucket extends ComparisonCounts {
  key: string;
  agreementRate: number | null;
}
export interface DisagreementSample {
  id: string; source: string; title: string | null;
  aiModelKey: string | null; regexModelKey: string | null;
  category: string | null; confidence: number;
}
export interface AiComparison {
  totalClassified: number;
  key: ComparisonCounts;
  category: ComparisonCounts;
  byCategory: ComparisonBucket[];
  bySource: ComparisonBucket[];
  outOfScope: Array<{ category: string; n: number }>;
  disagreements: DisagreementSample[];
  aiOnlyExamples: DisagreementSample[];
  regexOnlyExamples: DisagreementSample[];
}

export async function fetchAiComparison(limit = 12): Promise<AiComparison> {
  const r = await request<{ ok: true; comparison: AiComparison }>(
    `/v1/observe/ai/comparison?limit=${limit}`);
  return r.comparison;
}

export function saveAiCredit(creditGbp: number): Promise<{ ok: true; creditGbp: number }> {
  return request<{ ok: true; creditGbp: number }>('/v1/observe/ai/credit', {
    method: 'POST',
    body: { creditGbp },
  });
}

// ── Disagreements (design §9 step 2) ────────────────────────────────────────

export interface DisagreementRow {
  dealId: string | null;
  title: string | null;
  source: string;
  modelKey: string | null;
  category: string | null;
  ourDecision: string | null;
  ourReason: string | null;
  aiRating: string | null;
  aiReasoning: string | null;
  price: number | null;
}

export interface PriceGapRow {
  dealId: string | null;
  title: string | null;
  modelKey: string | null;
  listingPrice: number | null;
  ourAnchor: number | null;
  aiEstimate: number | null;
  /** Signed: positive means the model values it above our anchor. */
  gapPct: number;
}

export interface UnpricedModelRow {
  modelKey: string;
  processed: number;
  dropped: number;
}

/**
 * Whether the model's price estimate is its own, or an echo of the asking
 * price we showed it. Reported alongside the price panel rather than assumed —
 * an estimate that tracks the asking price agrees with us for the wrong reason.
 */
export interface PriceIndependence {
  samples: number;
  correlation: number | null;
  echoRate: number | null;
  verdict: 'independent' | 'suspect' | 'echoing' | 'insufficient_data';
}

export interface AiDisagreements {
  droppedButRated: DisagreementRow[];
  sentButPoor: DisagreementRow[];
  priceGaps: PriceGapRow[];
  unpricedModels: UnpricedModelRow[];
  priceIndependence: PriceIndependence;
}

export async function fetchAiDisagreements(limit = 25): Promise<AiDisagreements> {
  const r = await request<{ ok: true; disagreements: AiDisagreements }>(
    `/v1/observe/ai/disagreements?limit=${limit}`);
  return r.disagreements;
}

// ── Gate guard ──────────────────────────────────────────────────────────────

export interface GateGuardEntry {
  gate: string;
  samples: number;
  dropRate: number;
  tripped: boolean;
  trippedAt: string | null;
}

export interface AiGateGuard {
  ok: true;
  config: { maxDropRate: number; minSamples: number; windowSize: number };
  gates: GateGuardEntry[];
}

export function fetchAiGateGuard(): Promise<AiGateGuard> {
  return request<AiGateGuard>('/v1/observe/ai/gate-guard');
}
